import { NumberInput, Slider, Stack, Text, Title, useMantineTheme } from '@mantine/core'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { parseEther } from 'viem'

import RootButton from '@/components/Buttons/RootButton'
import CurrencyInput from '@/components/CurrencyInput'
import ErrorMessage from '@/components/ErrorMessage'
import { ConfirmationLoading, ConfirmationReady } from '@/components/Identity/ConfirmModals/utils'
import Loading from '@/components/Loading'
import StakeCalculator from '@/components/Staking/StakeCalculator'
import TransactionSummary from '@/components/Transfer/TransactionSummary'
import { MIN_STAKE_AMOUNT, MIN_STAKE_DAYS, STAKE_ADDRESS } from '@/constants/blockchain'
import { useEconomicsContext } from '@/contexts/EconomicsContext'
import { useStakingContext } from '@/contexts/StakingContext'
import StakeAbi from '@/contracts/Stake'
import { ContractCall, useChainFunctions } from '@/hooks/useChain'
import { l1NativeWagmiConfig } from '@/plugins/auth/config'
import { formatDays } from '@/utils/utils'

import ModalSkeleton from '../../ModalSkeleton'

interface StakeProps {
  close: () => void
}

const Stake: React.FC<StakeProps> = ({ close }) => {
  const { t } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.md})`)

  // modal
  const [openedConfirmation, { open: openConfirmation, close: closeConfirmation }] = useDisclosure()
  // fees - default value to setup minimum staking amount
  const estimatedFee = MIN_STAKE_AMOUNT
  // stake info
  const [amount, setAmount] = useState({ value: '', touched: false, error: '' })
  // ready confirmation
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [isReady, setIsReady] = useState<boolean>(false)

  const { writeContract } = useChainFunctions(l1NativeWagmiConfig)

  // Get staking and economics data
  const { totalStakeSupply, stakeMaxDays, stakeMultiplier, rl1PerDay, getCurrentDay, getDayStart } =
    useEconomicsContext()
  const { staked, stake } = useStakingContext()

  const [stakeDays, setStakeDays] = useState<number>(1)
  const contractCalls: ContractCall[] | undefined = useMemo(() => {
    if (!amount.value) {
      return undefined
    }
    return [
      {
        args: {
          address: STAKE_ADDRESS,
          abi: StakeAbi,
          functionName: 'lock',
          args: [stakeDays],
          value: parseEther(amount.value),
        },
      },
    ]
  }, [stakeDays, amount.value])

  const handleStake = useCallback(async () => {
    if (!(contractCalls && contractCalls.length > 0)) return
    try {
      setIsLoading(true)
      await writeContract(contractCalls[0].args)
      setIsLoading(false)
      setIsReady(true)
    } catch (err: any) {
      console.error(err)
      setIsLoading(false)
      setError(err.message)
    }
  }, [contractCalls, writeContract])

  // Compute the withdrawal date
  const today = useMemo(() => getCurrentDay(), [getCurrentDay])
  const withdrawalDate = useMemo(
    () => getDayStart(today + stakeDays + 1),
    [getDayStart, today, stakeDays]
  )

  const handleCloseConfirmationCancel = useCallback(() => {
    setError('')
    closeConfirmation()
  }, [closeConfirmation])

  const handleCloseConfirmation = useCallback(() => {
    setError('')
    closeConfirmation()
    close()
  }, [closeConfirmation, close])

  return (
    <>
      <Stack style={{ maxWidth: '900px' }}>
        <Text style={{ color: theme.colors.title[0] }}>
          {t(
            'components.staking.staking.modal.stakeDescription1',
            'Stake L1 to support the LAMINA1 network. In exchange, you’ll receive STAKE and VOTE tokens to increase your earning power, along with Rewards (RL1) tokens you can collect every day during the designated staking period.'
          )}
        </Text>
        <Stack style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
          <Stack>
            <Title order={4}>
              {t('components.staking.staking.modal.stake.stakeAmount', 'Stake Amount')}
            </Title>
            <CurrencyInput estimatedFee={estimatedFee} amount={amount} setAmount={setAmount} />
            <Title order={4} style={{ marginTop: '20px' }}>
              {t('components.staking.staking.modal.stake.stakePeriod', 'Stake Period')}
            </Title>
            <Stack style={{ flexDirection: 'row', gap: 20, alignItems: 'center' }}>
              <NumberInput
                style={{ width: '90px' }}
                allowDecimal={false}
                value={stakeDays > 0 ? stakeDays : ''}
                onChange={value => setStakeDays(Number(value))}
                min={MIN_STAKE_DAYS}
                max={stakeMaxDays}
                step={1}
                placeholder=""
                disabled={!!amount.error || !amount.value}
              />
              <Slider
                style={{ width: '100%', marginRight: 15 }}
                value={stakeDays}
                min={MIN_STAKE_DAYS}
                max={stakeMaxDays}
                step={1}
                precision={1}
                marks={[
                  { value: MIN_STAKE_DAYS, label: `${MIN_STAKE_DAYS} day` },
                  { value: stakeMaxDays, label: `${stakeMaxDays} days` },
                ]}
                label={(value: number) => (value === 1 ? `${value} day` : `${value} days`)}
                onChange={setStakeDays}
                disabled={!!amount.error || !amount.value}
              />
            </Stack>
            <Text style={{ fontSize: 14, color: theme.colors.textAndIcon[0] }}>
              <Trans i18nKey="components.staking.staking.modal.stake.stakingStatus">
                Enter or use slider to select a staking period between 1 day and{' '}
                {{ duration: formatDays(stakeMaxDays) }}.
              </Trans>
            </Text>
            {isMobile &&
              (staked && stake && totalStakeSupply ? (
                <StakeCalculator
                  amount={Number(amount.value)}
                  newStake={Number(amount.value) * stakeDays * stakeMultiplier}
                  stakeMaxDays={stakeMaxDays}
                  stakeMultiplier={stakeMultiplier}
                  currentStaked={staked}
                  currentStake={stake}
                  rl1PerDay={rl1PerDay}
                  stakeSupply={totalStakeSupply}
                />
              ) : (
                <Loading />
              ))}
            <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
              <RootButton onClick={close} secondary style1={false} expand>
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
              <RootButton
                disabled={!!amount.error || !amount.value || !stakeDays || stakeDays <= 0}
                onClick={openConfirmation}
                style1={false}
                expand
              >
                {t('components.staking.staking.modal.stake.stake', 'Stake')}
              </RootButton>
            </Stack>
            <TransactionSummary contractCalls={contractCalls} amount={amount.value} isStaking />
          </Stack>
          {!isMobile &&
            (staked && stake && totalStakeSupply ? (
              <StakeCalculator
                amount={Number(amount.value)}
                newStake={Number(amount.value) * stakeDays * stakeMultiplier}
                stakeMaxDays={stakeMaxDays}
                stakeMultiplier={stakeMultiplier}
                currentStaked={staked}
                currentStake={stake}
                rl1PerDay={rl1PerDay}
                stakeSupply={totalStakeSupply}
              />
            ) : (
              <Loading />
            ))}
        </Stack>
      </Stack>
      <ModalSkeleton
        opened={openedConfirmation}
        onClose={
          isReady && !isLoading && !error ? handleCloseConfirmation : handleCloseConfirmationCancel
        }
        title="Confirmation"
      >
        {error && (
          <ErrorMessage
            message={t(
              'components.staking.staking.modal.stake.errorMessage',
              'Error while staking'
            )}
            details={error}
          />
        )}
        {isLoading && (
          <ConfirmationLoading
            title={t('components.staking.staking.modal.stake.loadingMessage', 'Validating Stake')}
          />
        )}
        {isReady && !isLoading && !error && (
          <ConfirmationReady
            message={t('components.staking.staking.modal.stake.readyMessage', 'Stake Submitted')}
            tip={t(
              'components.staking.staking.modal.stake.tipMessage',
              'Check Wallet History for Transaction Status'
            )}
            success={handleCloseConfirmation}
          />
        )}
        {!isReady && !error && !isLoading && (
          <>
            <Text>
              <Trans i18nKey="components.staking.staking.modal.stake.confirmation">
                You are about to lock {{ amount: amount.value }} L1 for{' '}
                {{ numberStakeDays: stakeDays }} {{ stakeDays: stakeDays === 1 ? 'day' : 'days' }}.
                These L1 will not be accessible to you during the designated staking period. This
                stake will be available for withdrawal on {{ withdrawalDate }}.
              </Trans>
            </Text>
            <Stack style={{ flexDirection: 'row', marginTop: '20px' }}>
              <RootButton onClick={handleCloseConfirmationCancel} secondary style1={false} expand>
                {t('buttons.cancel', 'Cancel')}
              </RootButton>
              <RootButton
                disabled={!!amount.error || !amount.value || !stakeDays}
                onClick={handleStake}
                style1={false}
                expand
              >
                {t('buttons.confirm', 'Confirm')}
              </RootButton>
            </Stack>
          </>
        )}
      </ModalSkeleton>
    </>
  )
}

export default Stake
